import type {BasePanel} from '@Components/panel/panel.types';
import type {GridStockAudioItemStorage} from '@Components/base-grid/components/grid-stock-audio-item';
import {getUrlForVoicePreset} from '@Libraries/text-to-speech.library';

export interface AiVoicePanelProps extends BasePanel {
  allowedFilters?: PromptFilter[];
  onAddAudioItem(item: GridStockAudioItemStorage): Promise<void>;
  onClose?(): void;
  showBackButton?: boolean;
  contentClassname?: string;
}

export enum AiVoicePanelState {
  DEFAULT,
  AI_WRITER,
  RECENT_VOICE,
}

export enum PromptSubfilterType {
  STANDARD,
  PLAYABLE,
}

export interface BaseSubfilter {
  id: number;
  name: string;
  type: PromptSubfilterType;
}

export interface PlayableSubfilter extends BaseSubfilter {
  type: PromptSubfilterType.PLAYABLE;
  soundtrack: string;
}

export interface StandardSubfilter extends BaseSubfilter {
  type: PromptSubfilterType.STANDARD;
}

export type PromptSubfilter = PlayableSubfilter | StandardSubfilter;

export interface PromptFilter {
  id: PromptFilterId;
  name: string;
  subFilters: PromptSubfilter[];
}

export enum PromptFilterId {
  VOICE,
}

export interface SelectedPromptFilter {
  filterId: PromptFilterId;
  subfilterId: number;
}

export interface GeneratedAudioData {
  id: number;
  title: string;
  duration: number;
  source: string;
}

export enum AiVoicePanelHeightChangingEvents {
  DEFAULT = 'DEFAULT',
  GENERATING = 'GENERATING',
  GENERATED_RESULT = 'GENERATED_RESULT',
  EMPTY_RESULT = 'EMPTY_RESULT',
}

export enum AiVoicePanelErrorState {
  STANDARD,
  DAILY_LIMIT_REACHED,
}

export const DEFAULT_VOICE_SUBFILTER = 1;

export const VOICE_FILTER: PromptFilter = {
  id: PromptFilterId.VOICE,
  name: 'Voice Profile',
  subFilters: [
    {
      id: DEFAULT_VOICE_SUBFILTER,
      name: 'Jordan',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(DEFAULT_VOICE_SUBFILTER),
    },
    {
      id: 2,
      name: 'Taylor',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(2),
    },
    {
      id: 3,
      name: 'Chris',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(3),
    },
    {
      id: 4,
      name: 'James',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(4),
    },
    {
      id: 5,
      name: 'Allison',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(5),
    },
    {
      id: 6,
      name: 'Kristen',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(6),
    },
    {
      id: 7,
      name: 'Andrew',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(7),
    },
    {
      id: 8,
      name: 'Jodie',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(8),
    },
    {
      id: 9,
      name: 'Eleanor',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(9),
    },
  ],
};

export const PROMPT_FILTERS = [VOICE_FILTER];

export const PROMPT_CHARACTER_LIMIT = 2500;

export enum AiVoicePanelDataKey {
  STATE = 'state',
  GENERATION_STATE = 'generationState',
}

export enum AiVoicePanelGenerationState {
  IDLE = 'IDLE',
  GENERATING = 'GENERATING',
  READY = 'READY',
  ERROR = 'ERROR',
}

export const LOADING_KEY_PREFIX = 'ai-voice-loading';
